import { supabase } from '@/lib/supabase';

export type BookingDiscountType = 'fixed' | 'percent';

export interface BookingDiscount {
    id: number;
    booking_id: number;
    shop_id: number;
    type: BookingDiscountType;
    value: number;
    amount: number;
    applied_by: string | null;
    created_at: string;
}

export interface RecordBookingDiscountInput {
    booking_id: number;
    shop_id: number;
    type: BookingDiscountType;
    value: number;   // valore inserito dal dipendente (es. 10 = 10€, 20 = 20%)
    amount: number;  // euro di sconto risultanti (sempre positivo)
    applied_by: string | null; // uuid del dipendente che applica lo sconto (Hairdressers.id)
}

/**
 * Registra uno sconto applicato a una prenotazione (audit: chi/quando/quanto).
 * NB: lo specchio dell'importo su Bookings.price_adjustment è scritto da chi chiama
 * (EditAppointmentModal) così che web e mobile mostrino il prezzo finale scontato.
 */
export const recordBookingDiscount = async (
    input: RecordBookingDiscountInput
): Promise<{ success: boolean; error?: string; data?: BookingDiscount }> => {
    const { data, error } = await supabase
        .from('BookingDiscounts')
        .insert({
            booking_id: input.booking_id,
            shop_id: input.shop_id,
            type: input.type,
            value: input.value,
            amount: input.amount,
            applied_by: input.applied_by,
        })
        .select()
        .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data: data as BookingDiscount };
};

/**
 * Ritorna l'ultimo sconto registrato per una prenotazione (lo "stato corrente").
 */
export const fetchLatestBookingDiscount = async (
    bookingId: number
): Promise<BookingDiscount | null> => {
    const { data, error } = await supabase
        .from('BookingDiscounts')
        .select('*')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

    if (error || !data) return null;
    return data as BookingDiscount;
};
